import React, { useState, useEffect } from "react";
import { fetchBlog, createBlog, updateBlog, deleteBlog, resolveImg } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUploader from "./ImageUploader";
import { Loader2, Pencil, Trash2, Plus } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { slug: "", title: "", category: "Medals", date: "", author: "Best For Medals Team", image: "", excerpt: "", content: "" };

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const { toast } = useToast();

  const load = () => { setLoading(true); fetchBlog().then(setPosts).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing("new"); setForm({ ...empty, date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) }); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); };

  const save = async () => {
    if (!form.title) { toast({ title: "Title required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        const payload = { ...form, slug: form.slug || slugify(form.title) };
        await createBlog(payload);
        toast({ title: "Post created" });
      } else {
        await updateBlog(editing.slug, form);
        toast({ title: "Post updated" });
      }
      setEditing(null); load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await deleteBlog(delTarget.slug); toast({ title: "Post deleted" }); setDelTarget(null); load(); }
    catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-lg text-[#14171c]">Blog Posts</h2>
        <Button onClick={openNew} className="bg-[#C9A227] hover:bg-[#A6841B] text-white"><Plus className="w-4 h-4 mr-1" /> New Post</Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.slug} className="bg-white rounded-xl border border-neutral-200 p-3 flex items-center gap-4">
              <img src={resolveImg(p.image)} alt={p.title} className="w-20 h-16 rounded-lg object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#C9A227] font-bold">{p.category} • {p.date}</div>
                <div className="font-semibold text-[#14171c] truncate">{p.title}</div>
                <div className="text-sm text-neutral-500 truncate">{p.excerpt}</div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="h-8 text-xs"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => setDelTarget(p)} className="h-8 w-8 p-0 text-red-500 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Post" : "Edit Post"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-neutral-200 px-3 text-sm">
                  {["Medals", "Running", "Teamwear"].map((c) => (<option key={c}>{c}</option>))}
                </select>
              </div>
              <div><Label>Date</Label><Input className="mt-1.5" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            </div>
            {editing === "new" && (
              <div><Label>Slug (URL)</Label><Input className="mt-1.5" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder={slugify(form.title) || "auto-generated"} /></div>
            )}
            <ImageUploader label="Cover Image" value={form.image} onChange={(v) => set("image", v)} />
            <div><Label>Excerpt</Label><Textarea className="mt-1.5" rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></div>
            <div><Label>Content</Label><Textarea className="mt-1.5" rows={7} value={form.content} onChange={(e) => set("content", e.target.value)} placeholder="Separate paragraphs with a blank line." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete post?</AlertDialogTitle><AlertDialogDescription>This will permanently remove "{delTarget?.title}".</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBlog;
